import { Menu } from '@headlessui/react';
import {
  ArrowsExpandIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCopyIcon,
  DownloadIcon,
  SelectorIcon,
  XIcon,
} from '@heroicons/react/outline';
import type { Translate } from 'next-translate';
import useLocale from 'next-translate/useTranslation';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FaFileCsv, FaFileExcel } from 'react-icons/fa';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';
import { copyRows, saveBlob, toCsv } from '@/lib/spreadsheet/tableText';
import { numericValue } from '@/lib/spreadsheet/values';

import { buttonSecondary, chat, iconButton } from './chatUi';
import {
  DOCUMENT_MENU_PANEL_CLASS,
  DOCUMENT_MENU_WIDTH,
  MenuButtonItem,
  MenuPortal,
} from './MessageParts/documentMenu';

/*
 * A table in a Markdown reply, with the actions people reach for: copy it
 * (pastes into Excel and Google Sheets as cells), download it as CSV or
 * Excel, and open it full screen with sortable columns.
 */

/** The text of each cell, row by row (what Copy and Download use). */
function readRows(table: HTMLTableElement | null): string[][] {
  if (!table) return [];
  return Array.from(table.rows).map((row) =>
    Array.from(row.cells).map((cell) =>
      (cell.innerText ?? cell.textContent ?? '').replace(/\s+\n/g, '\n').trim(),
    ),
  );
}

/** A copy of a cell's content, without anything that could run code. */
function safeCellHtml(cell: HTMLTableCellElement): string {
  const copy = cell.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('*').forEach((node) => {
    for (const attribute of Array.from(node.attributes)) {
      const name = attribute.name.toLowerCase();
      if (name.startsWith('on')) node.removeAttribute(attribute.name);
      else if (
        (name === 'href' || name === 'src' || name === 'xlink:href') &&
        /^\s*(javascript|data|vbscript):/i.test(attribute.value) &&
        !(name === 'src' && /^\s*data:image\//i.test(attribute.value))
      ) {
        node.removeAttribute(attribute.name);
      }
    }
    if (node.tagName === 'SCRIPT' || node.tagName === 'IFRAME') node.remove();
  });
  return copy.innerHTML;
}

/**
 * The table's tools: 28 px kit icon buttons (40 px on touch screens). The
 * room above the table (`--md-table-toolbar-room` in globals.css) follows
 * these sizes: change both together.
 */
const TOOL_BUTTON = clsxm(
  iconButton,
  'h-7 w-7 rounded-md [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10',
);

function useCopy(getRows: () => string[][], t: Translate) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = useCallback(() => {
    copyRows(getRows())
      .then(() => {
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => toast.error(t('Couldn’t copy the table.')));
  }, [getRows, t]);
  return { copied, copy };
}

async function downloadRows(
  rows: string[][],
  format: 'csv' | 'xlsx',
  t: Translate,
) {
  if (rows.length === 0) return;
  try {
    if (format === 'csv') {
      saveBlob(
        new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }),
        'table.csv',
      );
      return;
    }
    const { writeXlsx } = await import('@/lib/spreadsheet/xlsxWriter');
    const blob = (await writeXlsx([{ name: 'Table', rows }])) as Blob;
    saveBlob(blob, 'table.xlsx');
  } catch {
    toast.error(t('Couldn’t create the file. Please try again.'));
  }
}

function DownloadMenu({
  getRows,
  className,
}: {
  getRows: () => string[][];
  className?: string;
}) {
  const { t } = useLocale('common');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  return (
    <Menu as='div' className='relative'>
      {({ open, close }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            aria-label={t('Download table')}
            title={t('Download')}
            className={className ?? TOOL_BUTTON}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Escape' && open) {
                e.preventDefault();
                close();
              }
            }}
          >
            <DownloadIcon className='h-4 w-4' />
          </Menu.Button>
          {open && (
            <MenuPortal anchorRef={buttonRef}>
              <Menu.Items
                static
                className={DOCUMENT_MENU_PANEL_CLASS}
                style={{ width: DOCUMENT_MENU_WIDTH }}
              >
                <MenuButtonItem
                  icon={<FaFileExcel className='h-4 w-4 text-[#107C41]' />}
                  label='Excel (.xlsx)'
                  hint={t('Numbers stay numbers')}
                  onSelect={() => void downloadRows(getRows(), 'xlsx', t)}
                />
                <MenuButtonItem
                  icon={
                    <FaFileCsv className='h-4 w-4 text-[#6f6f86] dark:text-slate-300' />
                  }
                  label='CSV'
                  hint={t('Opens in any spreadsheet app')}
                  onSelect={() => void downloadRows(getRows(), 'csv', t)}
                />
              </Menu.Items>
            </MenuPortal>
          )}
        </>
      )}
    </Menu>
  );
}

/* ------------------------------------------------------------------ */
/* Full-screen view                                                    */
/* ------------------------------------------------------------------ */

interface ExpandedTable {
  header: { html: string; text: string }[];
  body: { html: string; text: string }[][];
}

function readExpanded(table: HTMLTableElement): ExpandedTable {
  const rows = Array.from(table.rows);
  const toCells = (row: HTMLTableRowElement) =>
    Array.from(row.cells).map((cell) => ({
      html: safeCellHtml(cell),
      text: (cell.innerText ?? cell.textContent ?? '').trim(),
    }));
  const hasHeader = rows[0] && rows[0].parentElement?.tagName === 'THEAD';
  return {
    header: hasHeader ? toCells(rows[0]) : [],
    body: rows.slice(hasHeader ? 1 : 0).map(toCells),
  };
}

function compareText(a: string, b: string) {
  const na = numericValue(a);
  const nb = numericValue(b);
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  if (!a && b) return 1;
  if (a && !b) return -1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function ExpandedTableView({
  table,
  getRows,
  onClose,
}: {
  table: ExpandedTable;
  getRows: () => string[][];
  onClose: () => void;
}) {
  const { t } = useLocale('common');
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previousFocus?.focus?.();
    };
  }, [onClose]);

  const rows = useMemo(() => {
    if (!sort) return table.body;
    return [...table.body].sort(
      (a, b) =>
        compareText(a[sort.col]?.text ?? '', b[sort.col]?.text ?? '') *
        sort.dir,
    );
  }, [sort, table.body]);

  // Copy and Download take the rows in the order shown.
  const shownRows = useCallback(
    () =>
      sort
        ? [
            ...(table.header.length ? [table.header.map((c) => c.text)] : []),
            ...rows.map((row) => row.map((c) => c.text)),
          ]
        : getRows(),
    [getRows, rows, sort, table.header],
  );
  const { copied, copy } = useCopy(shownRows, t);

  const toggleSort = (col: number) =>
    setSort((current) =>
      !current || current.col !== col
        ? { col, dir: 1 }
        : current.dir === 1
          ? { col, dir: -1 }
          : null,
    );

  return createPortal(
    <div
      className='fixed inset-0 z-[110] flex items-stretch justify-center bg-[#0f0f28]/40 backdrop-blur-[2px] dark:bg-black/60 sm:p-6'
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-label={t('Table')}
        className='flex h-full w-full max-w-6xl flex-col overflow-hidden bg-white shadow-[0_24px_64px_-24px_rgba(16,16,40,0.45)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-slate-900 dark:ring-white/10 sm:rounded-2xl'
      >
        <div className='flex items-center gap-2 border-b border-[#0f0f28]/[0.07] px-4 py-3 dark:border-white/[0.08]'>
          <div className='min-w-0 flex-1'>
            <div className={clsxm('text-[14px] font-semibold', chat.ink)}>
              {t('Table')}
            </div>
            <div className={clsxm('text-[12px]', chat.quiet)}>
              {table.body.length === 1
                ? t('1 row')
                : t(
                    '{{rows}} rows',
                    { rows: table.body.length.toLocaleString() },
                    { default: '{{rows}} rows' },
                  )}
              {table.header.length > 0 && ` · ${t('Click a column to sort')}`}
            </div>
          </div>
          <button
            type='button'
            onClick={copy}
            className={clsxm(buttonSecondary, 'h-8 px-3')}
          >
            {copied ? (
              <CheckIcon
                className='h-4 w-4 text-emerald-600 dark:text-emerald-400'
                aria-hidden='true'
              />
            ) : (
              <ClipboardCopyIcon className='h-4 w-4' aria-hidden='true' />
            )}
            {copied ? t('Copied') : t('Copy')}
          </button>
          <DownloadMenu
            getRows={shownRows}
            className={clsxm(
              iconButton,
              'ring-1 ring-[#0f0f28]/10 dark:ring-white/15',
            )}
          />
          <button
            ref={closeRef}
            type='button'
            onClick={onClose}
            aria-label={t('Close')}
            className={iconButton}
          >
            <XIcon className='h-5 w-5' aria-hidden='true' />
          </button>
        </div>
        <div className='min-h-0 flex-1 overflow-auto'>
          <table className='chat-table-expanded'>
            {table.header.length > 0 && (
              <thead>
                <tr>
                  {table.header.map((cell, i) => (
                    <th
                      key={i}
                      aria-sort={
                        sort?.col === i
                          ? sort.dir === 1
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <button
                        type='button'
                        onClick={() => toggleSort(i)}
                        className='flex w-full items-center gap-1 text-start'
                        title={t('Sort')}
                      >
                        <span dangerouslySetInnerHTML={{ __html: cell.html }} />
                        <span className='ms-auto flex-shrink-0 ps-1 text-[#8b8ba0] dark:text-slate-500'>
                          {sort?.col === i ? (
                            sort.dir === 1 ? (
                              <ChevronUpIcon className='h-3.5 w-3.5 text-primary-600' />
                            ) : (
                              <ChevronDownIcon className='h-3.5 w-3.5 text-primary-600' />
                            )
                          ) : (
                            <SelectorIcon className='h-3.5 w-3.5' />
                          )}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      dangerouslySetInnerHTML={{ __html: cell.html }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Table                                                               */
/* ------------------------------------------------------------------ */

export default function ChatTable({ children }: { children: React.ReactNode }) {
  const { t } = useLocale('common');
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [expanded, setExpanded] = useState<ExpandedTable | null>(null);
  const getRows = useCallback(() => readRows(tableRef.current), []);
  const { copied, copy } = useCopy(getRows, t);
  const close = useCallback(() => setExpanded(null), []);

  return (
    <div className='md-table-block group/table relative'>
      <div className='md-table-toolbar absolute bottom-full end-0 mb-1 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/table:opacity-100 [@media(hover:none)]:opacity-100'>
        <button
          type='button'
          onClick={copy}
          aria-label={t('Copy table')}
          title={
            copied ? t('Copied') : t('Copy (pastes into Excel and Sheets)')
          }
          className={TOOL_BUTTON}
        >
          {copied ? (
            <CheckIcon
              className='h-4 w-4 text-emerald-600 dark:text-emerald-400'
              aria-hidden='true'
            />
          ) : (
            <ClipboardCopyIcon className='h-4 w-4' aria-hidden='true' />
          )}
        </button>
        <DownloadMenu getRows={getRows} />
        <button
          type='button'
          onClick={() =>
            tableRef.current && setExpanded(readExpanded(tableRef.current))
          }
          aria-label={t('Open full screen')}
          title={t('Full screen')}
          className={TOOL_BUTTON}
        >
          <ArrowsExpandIcon className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
      <div className='md-table-wrapper'>
        <table ref={tableRef}>{children}</table>
      </div>
      {expanded && (
        <ExpandedTableView table={expanded} getRows={getRows} onClose={close} />
      )}
    </div>
  );
}
